import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import actionDetalhesUsuario from 'src/actions/usuarios/actionDetalhesUsuario';
import getActionListaUsuariosByClienteId from 'src/actions/usuarios/getActionListaUsuariosByClienteId';
import v2_actionSyncUsuariosImoview from 'src/actions/v2/ai/v2_actionSyncUsuariosImoview';
import v2_actionListasImoview from 'src/actions/v2/ai/v2_actionListasImoview';
import { Alert, AlertDescription } from 'src/@/components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'src/@/components/ui/accordion';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Switch } from 'src/@/components/ui/switch';
import { Separator } from 'src/@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { RadioGroup, RadioGroupItem } from 'src/@/components/ui/radio-group';
import { MultiSelect } from 'src/@/components/ui/multi-select';
import ConciliacaoImoviewSection from './ConciliacaoImoviewSection';

const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';
const PASSO_LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-[#7C3AED]';

// Ícone de "vinculado corretamente no Imoview" — só entra em corretorOptions,
// que já é filtrado pra só ter quem tem codigoUsuario confirmado (ver
// usuariosMapeados abaixo), então todo item aqui sempre está conciliado.
function CorretorVinculadoIcon(props) {
    return <Iconify icon="eva:checkmark-circle-2-fill" {...props} className={`text-green-600 ${props.className || ''}`} />;
}

export default function EnvioAtendimentoSection({ aiAgent, changeModuleConfig }) {
    const { t } = useTranslation('settings');
    const { cliente, user } = useAuth();

    const realstate = aiAgent?.config?.module?.realstate || {};
    const funcoes = realstate.funcoes || {};
    const cfg = funcoes.envioAtendimento || {};
    const corretor = cfg.corretor || { tipo: 'fixo', usuarioId: null, usuariosRoleta: [] };

    const [revelarSenha, setRevelarSenha] = useState(false);
    const [minhaContaImoviewEmail, setMinhaContaImoviewEmail] = useState('');
    const [usuarios, setUsuarios] = useState([]);
    const [sincronizando, setSincronizando] = useState(false);
    const [resultadoSync, setResultadoSync] = useState(null); // { ok, msg }
    const [listas, setListas] = useState({ unidades: [], midias: [], tiposAtendimento: [] });
    const [carregandoListas, setCarregandoListas] = useState(false);
    const [resultadoListas, setResultadoListas] = useState(null); // { ok, msg }
    const [ultimoResumo, setUltimoResumo] = useState(null);

    const updateCfg = (patch) => {
        changeModuleConfig({
            funcoes: {
                ...funcoes,
                envioAtendimento: { ...cfg, ...patch },
            },
        });
    };

    const updateCorretor = (patch) => {
        updateCfg({ corretor: { ...corretor, ...patch } });
    };

    async function buscarUsuarios() {
        if (!cliente?._id) return;
        try {
            const result = await getActionListaUsuariosByClienteId(cliente._id);
            const lista = result?.data?.usuarios || [];
            setUsuarios(Array.isArray(lista) ? lista : []);
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        buscarUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cliente?._id]);

    // Além de re-buscar os usuários (pra refletir o novo config.imoview no
    // dropdown de corretor e no Passo 3), move quem acabou de ganhar senha
    // de "pendente" pro estado resolvido no resumo local — assim o Passo 2/3
    // mostra o estado atual sem precisar carregar tudo de novo.
    const handlePareado = async (pareados) => {
        if (Array.isArray(pareados) && pareados.length) {
            const idsRecemPareados = new Set(pareados.map((p) => p.laisId));
            setUltimoResumo((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    laisSemImoview: (prev.laisSemImoview || []).filter((lu) => !idsRecemPareados.has(lu.laisId)),
                    detalheCasados: [...(prev.detalheCasados || []), ...pareados],
                };
            });
        }
        await buscarUsuarios();
    };

    // Evita pedir a mesma credencial duas vezes: quem configura o agente e já
    // salvou o próprio email Imoview em Minha Conta não precisa redigitá-lo
    // aqui — só pré-preenche (não sobrescreve um email já salvo na config do
    // agente). A senha continua precisando ser digitada; só guardamos o hash
    // MD5 dela em Minha Conta, não dá pra reaproveitar aqui.
    useEffect(() => {
        if (!user?.id) return;
        actionDetalhesUsuario(user.id).then((res) => {
            const emailPessoal = res?.data?.data?.config?.imoview?.email;
            if (emailPessoal) setMinhaContaImoviewEmail(emailPessoal);
        }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    const emailEfetivo = cfg.email || minhaContaImoviewEmail || '';
    const usandoContaPessoal = !cfg.email && !!minhaContaImoviewEmail;
    // A chave agora é configurada uma vez na aba "Integração CRM" e escrita
    // nos dois blocos (envioCrmParceiro/envioAtendimento) — mas agentes
    // configurados antes dessa mudança podem ter a chave só em
    // envioCrmParceiro.token (era o fallback que o backend já fazia). Mesma
    // resolução aqui, senão "Carregar" fica preso desabilitado.
    const tokenEfetivo = cfg.token || funcoes.envioCrmParceiro?.token || '';

    // Corretor responsável exige UMA coisa: identidade CONCILIADA no Imoview
    // (config.imoview.codigoUsuario — via sync automático ou pareamento
    // manual, seção "Usuários já sincronizados"/Passo 2 acima). A senha
    // pessoal do corretor NÃO entra mais no critério: quem cria o atendimento
    // é a conta fixa do Passo 1, e o corretor só recebe a transferência
    // (App_AlterarCorretor), que identifica o destino pelo codigoUsuario.
    const usuariosMapeados = useMemo(
        () => usuarios.filter((u) => u?.config?.imoview?.codigoUsuario != null),
        [usuarios],
    );

    // O código Imoview entra no label porque é ele que vai como
    // `codigoNovoCorretor` na transferência do atendimento — dá pra conferir
    // aqui mesmo contra o cadastro do convênio, sem abrir o banco.
    const corretorOptions = useMemo(
        () => usuariosMapeados.map((u) => ({
            value: String(u._id),
            label: `${u.nomeFantasia || u.firstName || u.email || t('aiAgents.realstate.envioAtendimento.noNameFallback', '(sem nome)')} · #${u.config.imoview.codigoUsuario}`,
            icon: CorretorVinculadoIcon,
        })),
        [usuariosMapeados, t],
    );

    async function sincronizar() {
        if (!aiAgent?._id) return;
        setSincronizando(true);
        setResultadoSync(null);
        try {
            const result = await v2_actionSyncUsuariosImoview(aiAgent._id, { email: emailEfetivo, senha: cfg.senha });
            const resumo = result?.data?.resumo;
            setResultadoSync({
                ok: true,
                msg: t(
                    'aiAgents.realstate.envioAtendimento.syncSuccess',
                    '{{casados}} usuário(s) mapeado(s) de {{imoviewTotal}} do Imoview.',
                    { casados: resumo?.casados ?? 0, imoviewTotal: resumo?.imoviewTotal ?? 0 },
                ),
            });
            setUltimoResumo(resumo || null);
            await buscarUsuarios();
        } catch (error) {
            const msg = error?.response?.data?.message
                || t('aiAgents.realstate.envioAtendimento.syncErrorFallback', 'Falha ao sincronizar. Verifique email/senha.');
            setResultadoSync({ ok: false, msg });
        } finally {
            setSincronizando(false);
        }
    }

    // Unidades vêm da API de parceiro (só a chave); mídias e tipos exigem o
    // login da conta de serviço. Sem esses códigos o atendimento é recusado
    // pela Imoview com 404 "Informe um lead válido!".
    async function carregarListas() {
        if (!aiAgent?._id) return;
        setCarregandoListas(true);
        setResultadoListas(null);
        try {
            const result = await v2_actionListasImoview(aiAgent._id, {
                email: emailEfetivo,
                senha: cfg.senha,
                token: tokenEfetivo,
            });
            const data = result?.data || {};
            setListas({
                unidades: data.unidades || [],
                midias: data.midias || [],
                tiposAtendimento: data.tiposAtendimento || [],
            });
            setResultadoListas({
                ok: true,
                msg: data.aviso
                    || t(
                        'aiAgents.realstate.envioAtendimento.listsSuccess',
                        '{{unidades}} unidade(s), {{midias}} mídia(s) e {{tipos}} tipo(s) carregado(s).',
                        {
                            unidades: (data.unidades || []).length,
                            midias: (data.midias || []).length,
                            tipos: (data.tiposAtendimento || []).length,
                        },
                    ),
            });
        } catch (error) {
            const msg = error?.response?.data?.message
                || t('aiAgents.realstate.envioAtendimento.listsErrorFallback', 'Falha ao carregar as listas. Verifique chave, email e senha.');
            setResultadoListas({ ok: false, msg });
        } finally {
            setCarregandoListas(false);
        }
    }

    // Passo 1 dispara os dois de uma vez pro usuário — antes eram dois
    // botões separados ("Carregar listas" e "Sincronizar usuários"). MAS
    // precisa ser em SEQUÊNCIA, não em paralelo: os dois fazem login
    // independente na Imoview com a mesma conta (App_ValidarAcesso), e a
    // API parece invalidar a sessão anterior quando um segundo login
    // acontece quase ao mesmo tempo — rodando em paralelo (Promise.all), um
    // dos dois login derrubava a sessão do outro no meio da chamada,
    // gerando "Falha ao sincronizar usuários" mesmo com credenciais certas.
    const carregando = carregandoListas || sincronizando;
    async function carregarTudo() {
        await carregarListas();
        await sincronizar();
    }

    // Parceiro é escolhido uma única vez na aba "Integração CRM" (fora deste
    // bloco) e replicado para cfg.parceiro — aqui só checamos se já veio.
    const parceiroSelecionado = cfg.parceiro === 'imoview';
    const tipoCorretor = corretor.tipo === 'roleta' ? 'roleta' : 'fixo';
    const ativoSemParceiro = !!cfg.ativo && !cfg.parceiro;
    const ativoSemCredenciais = !!cfg.ativo && !!cfg.parceiro && (!emailEfetivo || !cfg.senha);
    const ativoSemCorretorFixo = !!cfg.ativo && tipoCorretor === 'fixo' && !corretor.usuarioId;
    const ativoSemRoleta = !!cfg.ativo && tipoCorretor === 'roleta'
        && (!Array.isArray(corretor.usuariosRoleta) || corretor.usuariosRoleta.length === 0);
    const podeCarregar = !!emailEfetivo && !!cfg.senha && !!tokenEfetivo && !carregando;
    // Mídia e tipo não são mais config estática (resolvidos por lead em
    // enviarAtendimentoParceiro) — só falta unidade mesmo.
    const codigosFaltando = [
        !cfg.unidadecodigo && t('aiAgents.realstate.envioAtendimento.missingUnidade', 'unidade'),
    ].filter(Boolean);
    const ativoSemCodigos = !!cfg.ativo && !!cfg.parceiro && codigosFaltando.length > 0;

    // Carrinho do atendimento: depois da transferência, a Imoview só aceita
    // App_IncluirImovelCarrinho vindo do DONO do atendimento — o corretor. Aí a
    // senha pessoal dele passa a importar (o backend autentica com
    // config.imoview.email + senhaMd5), ao contrário do dropdown acima, que só
    // exige o codigoUsuario. Sem senha o carrinho falha a cada turno e o
    // corretor abre o atendimento vazio, sem ninguém perceber — avisamos aqui.
    const corretoresSelecionadosIds = tipoCorretor === 'roleta'
        ? (Array.isArray(corretor.usuariosRoleta) ? corretor.usuariosRoleta.map(String) : [])
        : (corretor.usuarioId ? [String(corretor.usuarioId)] : []);
    const corretoresSemSenhaImoview = !!cfg.carrinho?.ativo
        ? usuarios.filter((u) => corretoresSelecionadosIds.includes(String(u._id))
            && !(u?.config?.imoview?.email && u?.config?.imoview?.senhaMd5))
        : [];
    const nomeUsuario = (u) => u.nomeFantasia || u.firstName || u.email
        || t('aiAgents.realstate.envioAtendimento.noNameFallback', '(sem nome)');

    return (
        <Accordion
            type="single"
            collapsible
            className="rounded-xl border border-gray-100 bg-white"
        >
            <AccordionItem value="envio-atendimento" className="border-0">
                <AccordionTrigger className="hover:no-underline px-6 py-4 [&[data-state=open]]:pb-3">
                    <div className="flex items-start justify-between gap-3 flex-1 pr-3 text-left">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50 shrink-0">
                                <Iconify icon="mdi:account-tie-outline" width={20} className="text-violet-600" />
                            </div>
                            <div>
                                <div className="text-base font-semibold text-gray-900 leading-tight">
                                    {t('aiAgents.realstate.envioAtendimento.title', 'Envio de Atendimento ao CRM Parceiro')}
                                </div>
                                <div className="text-sm text-gray-500 font-normal mt-1">
                                    {t(
                                        'aiAgents.realstate.envioAtendimento.description',
                                        'Quando todas as perguntas qualificatórias forem respondidas, a Lais abre um atendimento no CRM da imobiliária parceira e guarda o código do atendimento.',
                                    )}
                                </div>
                            </div>
                        </div>
                        <div
                            role="presentation"
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="shrink-0"
                        >
                            <Switch
                                checked={!!cfg.ativo}
                                onCheckedChange={(value) => updateCfg({ ativo: value })}
                            />
                        </div>
                    </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                    {!cfg.ativo ? (
                        <p className="text-xs text-gray-500 italic">
                            {t('aiAgents.realstate.envioAtendimento.inactiveHint', 'Ative o recurso acima para configurar.')}
                        </p>
                    ) : (
                <div className="flex flex-col gap-4">
                        {ativoSemParceiro && (
                            <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                                <AlertDescription className="text-xs">
                                    {t('aiAgents.realstate.envioAtendimento.selectPartnerWarning', 'Selecione um parceiro na aba "Integração CRM" acima para a integração começar a funcionar.')}
                                </AlertDescription>
                            </Alert>
                        )}

                        {parceiroSelecionado && (
                            <>
                                {/* Passo 1 — Credenciais + Unidade + Carregar */}
                                <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col gap-3">
                                    <div>
                                        <Label className={PASSO_LABEL_CLS}>
                                            {t('aiAgents.realstate.envioAtendimento.passo1Label', 'Passo 1 — Credenciais e unidade')}
                                        </Label>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {t(
                                                'aiAgents.realstate.envioAtendimento.credenciaisServicoHint',
                                                'Email e senha de um usuário Imoview com permissão para criar atendimentos. É esta conta que abre cada atendimento e depois o transfere para o corretor responsável (configurado no fim desta tela). Também é usada para carregar a unidade e sincronizar os usuários do convênio abaixo.',
                                            )}
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex flex-col gap-2">
                                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                                {t('aiAgents.realstate.envioAtendimento.emailLabel', 'Email da conta Imoview')}
                                            </Label>
                                            <Input
                                                type="email"
                                                placeholder={t('aiAgents.realstate.envioAtendimento.emailPlaceholder', 'conta@imobiliaria.com.br')}
                                                value={emailEfetivo}
                                                onChange={(e) => updateCfg({ email: e.target.value })}
                                                className={`${INPUT_CLS} ${ativoSemCredenciais && !emailEfetivo ? 'border-amber-300' : ''}`}
                                            />
                                            {usandoContaPessoal && (
                                                <p className="text-xs text-gray-500">
                                                    {t(
                                                        'aiAgents.realstate.envioAtendimento.usandoContaPessoalHint',
                                                        'Usando o email da sua conta pessoal (Minha Conta → Imoview). Digite outro email aqui para usar uma conta de serviço diferente.',
                                                    )}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                                {t('aiAgents.realstate.envioAtendimento.passwordLabel', 'Senha')}
                                            </Label>
                                            <div className="relative">
                                                <Input
                                                    type={revelarSenha ? 'text' : 'password'}
                                                    placeholder={t('aiAgents.realstate.envioAtendimento.passwordPlaceholder', 'Senha da conta')}
                                                    value={cfg.senha || ''}
                                                    onChange={(e) => updateCfg({ senha: e.target.value })}
                                                    className={`${INPUT_CLS} pr-10 ${ativoSemCredenciais && !cfg.senha ? 'border-amber-300' : ''}`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setRevelarSenha((v) => !v)}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                                    tabIndex={-1}
                                                >
                                                    <Iconify icon={revelarSenha ? 'eva:eye-off-outline' : 'eva:eye-outline'} width={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-2 max-w-md">
                                        <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                            {t('aiAgents.realstate.envioAtendimento.unidadeLabel', 'Unidade')}
                                        </Label>
                                        <Select
                                            value={cfg.unidadecodigo ? String(cfg.unidadecodigo) : ''}
                                            onValueChange={(value) => updateCfg({ unidadecodigo: Number(value) })}
                                        >
                                            <SelectTrigger className={INPUT_CLS}>
                                                <SelectValue placeholder={t('aiAgents.realstate.envioAtendimento.selectGenericPlaceholder', 'Selecione...')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {listas.unidades.map((u) => (
                                                    <SelectItem key={u.codigo} value={String(u.codigo)}>{u.nome}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {ativoSemCodigos && (
                                            <p className="text-xs text-amber-700">
                                                {t(
                                                    'aiAgents.realstate.envioAtendimento.missingCodesWarning',
                                                    'Faltando: {{lista}}. Sem esses códigos a Imoview recusa o atendimento.',
                                                    { lista: codigosFaltando.join(', ') },
                                                )}
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={carregarTudo}
                                            disabled={!podeCarregar}
                                            className={`flex items-center gap-2 px-3 py-2 text-white text-xs font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 shrink-0 ${
                                                podeCarregar ? 'bg-[#7C3AED] hover:bg-[#6d28d9] active:scale-95' : 'bg-gray-300 cursor-not-allowed'
                                            }`}
                                        >
                                            <Iconify icon={carregando ? 'eva:loader-outline' : 'eva:download-outline'} width={14} className={carregando ? 'animate-spin' : ''} />
                                            {carregando ? t('buttons.loading', { ns: 'common' }) : t('aiAgents.realstate.envioAtendimento.loadButton', 'Carregar')}
                                        </button>
                                    </div>

                                    {resultadoListas && (
                                        <Alert className={resultadoListas.ok ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}>
                                            <AlertDescription className="text-xs">{resultadoListas.msg}</AlertDescription>
                                        </Alert>
                                    )}
                                    {resultadoSync && (
                                        <Alert className={resultadoSync.ok ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}>
                                            <AlertDescription className="text-xs">{resultadoSync.msg}</AlertDescription>
                                        </Alert>
                                    )}
                                </div>

                                <ConciliacaoImoviewSection
                                    resumo={ultimoResumo}
                                    usuarios={usuarios}
                                    onPareado={handlePareado}
                                />

                                <Separator className="bg-gray-200/60" />

                                <div className="flex flex-col gap-3">
                                    <div>
                                        <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                            {t('aiAgents.realstate.envioAtendimento.corretorLabel', 'Corretor responsável')}
                                        </Label>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {t('aiAgents.realstate.envioAtendimento.corretorHint', 'Cada atendimento é criado pela conta do Passo 1 e transferido para o corretor escolhido aqui. Só aparecem usuários já identificados no Imoview (Passo 2) — não é preciso a senha pessoal deles.')}
                                        </p>
                                    </div>

                                    <RadioGroup
                                        value={tipoCorretor}
                                        onValueChange={(value) => updateCorretor({ tipo: value })}
                                        className="flex flex-col gap-2"
                                    >
                                        <label
                                            htmlFor="corretor-fixo"
                                            className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                                                tipoCorretor === 'fixo' ? 'border-[#7C3AED] bg-violet-50/50' : 'border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <RadioGroupItem value="fixo" id="corretor-fixo" className="mt-1" />
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {t('aiAgents.realstate.envioAtendimento.corretorFixoLabel', 'Corretor fixo')}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    {t('aiAgents.realstate.envioAtendimento.corretorFixoDesc', 'Todos os atendimentos criados pela IA ficam com o mesmo corretor.')}
                                                </span>
                                            </div>
                                        </label>
                                        <label
                                            htmlFor="corretor-roleta"
                                            className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                                                tipoCorretor === 'roleta' ? 'border-[#7C3AED] bg-violet-50/50' : 'border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <RadioGroupItem value="roleta" id="corretor-roleta" className="mt-1" />
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {t('aiAgents.realstate.envioAtendimento.corretorRoletaLabel', 'Roleta entre corretores')}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    {t('aiAgents.realstate.envioAtendimento.corretorRoletaDesc', 'Distribui os atendimentos em rotação (round-robin) entre os corretores selecionados.')}
                                                </span>
                                            </div>
                                        </label>
                                    </RadioGroup>

                                    {corretorOptions.length === 0 && (
                                        <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                                            <AlertDescription className="text-xs">
                                                {t(
                                                    'aiAgents.realstate.envioAtendimento.noMappedUsersWarning',
                                                    'Nenhum usuário disponível ainda — clique em "Carregar" no Passo 1 e pareie os usuários no Passo 2 para ter corretores identificados no Imoview.',
                                                )}
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    {tipoCorretor === 'fixo' && corretorOptions.length > 0 && (
                                        <div className="flex flex-col gap-2 max-w-md">
                                            <Select
                                                value={corretor.usuarioId ? String(corretor.usuarioId) : ''}
                                                onValueChange={(value) => updateCorretor({ usuarioId: value || null })}
                                            >
                                                <SelectTrigger className={INPUT_CLS}>
                                                    <SelectValue placeholder={t('aiAgents.realstate.envioAtendimento.selectCorretorPlaceholder', 'Selecione um corretor...')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {corretorOptions.map((c) => (
                                                        <SelectItem key={c.value} value={c.value}>
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <CorretorVinculadoIcon width={14} />
                                                                {c.label}
                                                            </span>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {ativoSemCorretorFixo && (
                                                <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                                                    <AlertDescription className="text-xs">
                                                        {t('aiAgents.realstate.envioAtendimento.selectCorretorFixoWarning', 'Selecione um corretor responsável para os atendimentos.')}
                                                    </AlertDescription>
                                                </Alert>
                                            )}
                                        </div>
                                    )}

                                    {tipoCorretor === 'roleta' && corretorOptions.length > 0 && (
                                        <div className="flex flex-col gap-2">
                                            <MultiSelect
                                                options={corretorOptions}
                                                onValueChange={(values) => updateCorretor({ usuariosRoleta: values })}
                                                defaultValue={Array.isArray(corretor.usuariosRoleta) ? corretor.usuariosRoleta.map(String) : []}
                                                placeholder={t('aiAgents.realstate.envioAtendimento.selectCorretoresRoletaPlaceholder', 'Selecione os corretores...')}
                                                variant="inverted"
                                                animation={2}
                                                maxCount={5}
                                            />
                                            {ativoSemRoleta && (
                                                <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                                                    <AlertDescription className="text-xs">
                                                        {t('aiAgents.realstate.envioAtendimento.selectCorretoresRoletaWarning', 'Selecione pelo menos um corretor para a roleta.')}
                                                    </AlertDescription>
                                                </Alert>
                                            )}
                                            {!ativoSemRoleta && (
                                                <p className="text-xs text-gray-500">
                                                    {t('aiAgents.realstate.envioAtendimento.roletaOrderHint', 'Cada novo atendimento é atribuído ao próximo corretor da lista, em rotação.')}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <Separator className="bg-gray-200/60" />

                                {/* Handoff na LAIS: o card vai pro corretor sempre; o chat depende deste flag */}
                                <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col gap-3">
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                                {t('aiAgents.realstate.envioAtendimento.transferirChatLabel', 'Transferir a conversa na LAIS ao criar o atendimento')}
                                            </Label>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {t(
                                                    'aiAgents.realstate.envioAtendimento.transferirChatHint',
                                                    'Assim que o atendimento for criado e transferido ao corretor no Imoview, a conversa do WhatsApp vai para esse mesmo usuário na LAIS e a Lais encerra o atendimento com uma mensagem de encaminhamento. O card do funil vai para o corretor em qualquer caso.',
                                                )}
                                            </p>
                                        </div>
                                        <div className="shrink-0 pt-0.5">
                                            <Switch
                                                checked={!!cfg.transferirChatAoCriar}
                                                onCheckedChange={(value) => updateCfg({ transferirChatAoCriar: value })}
                                            />
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        {cfg.transferirChatAoCriar
                                            ? t(
                                                'aiAgents.realstate.envioAtendimento.transferirChatOnHint',
                                                'A Lais não envia sugestões de imóveis depois de abrir o atendimento — o corretor assume a conversa. Imóveis citados pelo cliente antes disso ainda entram no carrinho.',
                                            )
                                            : t(
                                                'aiAgents.realstate.envioAtendimento.transferirChatOffHint',
                                                'A Lais continua atendendo (sugestões, carrinho) e só transfere a conversa quando um gatilho da aba Transferência disparar — nesse momento o destino é o corretor do atendimento, não o setor/usuário configurado lá.',
                                            )}
                                    </p>
                                </div>

                                {/* Carrinho do atendimento (App_IncluirImovelCarrinho) */}
                                <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col gap-3">
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                                {t('aiAgents.realstate.envioAtendimento.carrinhoLabel', 'Incluir imóveis no carrinho')}
                                            </Label>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {t(
                                                    'aiAgents.realstate.envioAtendimento.carrinhoHint',
                                                    'Os imóveis em que o cliente demonstrou interesse explícito — mandou o código ou o link, ou disse que gostou de uma das sugestões — entram no carrinho do atendimento. É o que o corretor vê ao abrir o atendimento no Imoview. Imóvel apenas ofertado, que o cliente ignorou, não entra.',
                                                )}
                                            </p>
                                        </div>
                                        <div className="shrink-0 pt-0.5">
                                            <Switch
                                                checked={!!cfg.carrinho?.ativo}
                                                onCheckedChange={(value) => updateCfg({ carrinho: { ...(cfg.carrinho || {}), ativo: value } })}
                                            />
                                        </div>
                                    </div>
                                    {!!cfg.carrinho?.ativo && (
                                        <p className="text-xs text-gray-500">
                                            {t(
                                                'aiAgents.realstate.envioAtendimento.carrinhoPendingHint',
                                                'Se o cliente demonstrar interesse antes de o atendimento ser aberto, os imóveis ficam pendentes e entram no carrinho assim que a qualificação fechar.',
                                            )}
                                        </p>
                                    )}
                                    {corretoresSemSenhaImoview.length > 0 && (
                                        <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                                            <AlertDescription className="text-xs">
                                                {t(
                                                    'aiAgents.realstate.envioAtendimento.carrinhoSemSenhaWarning',
                                                    'Depois da transferência, o carrinho é gravado com a sessão do corretor — para isso ele precisa ter email e senha Imoview salvos (Passo 3 acima ou Minha Conta → Imoview). Ainda sem senha: {{nomes}}. Para esses corretores os imóveis não entrarão no carrinho.',
                                                    { nomes: corretoresSemSenhaImoview.map(nomeUsuario).join(', ') },
                                                )}
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                </div>
                            </>
                        )}

                        <Separator className="bg-gray-200/60" />

                        <Alert className="bg-violet-50 border-violet-200 text-violet-800">
                            <AlertDescription className="text-xs">
                                <Trans t={t} i18nKey="aiAgents.realstate.envioAtendimento.idempotentNote" components={{ strong: <strong /> }}>
                                    <strong>Idempotente:</strong> o atendimento é aberto uma única vez. O código do atendimento
                                    retornado pelo parceiro fica guardado no lead para requisições futuras (interações,
                                    agendamento de visita). Status e resposta ficam registrados no histórico do lead.
                                </Trans>
                            </AlertDescription>
                        </Alert>
                </div>
                    )}
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}
